import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { QaRunDetail, QaRunListItem } from '../../types/qa';
import { startQaRun, fetchQaRun, fetchQaRuns, fetchAllQaRuns } from './actions';

interface QaState {
  runs: QaRunListItem[];
  allRuns: QaRunListItem[];
  allRunsTotal: number;
  allRunsPage: number;
  allRunsFlowFilter: string | null;
  currentRun: QaRunDetail | null;
  pendingRunId: string | null;
  loading: boolean;
  historyLoading: boolean;
  starting: boolean;
  error: string | null;
}

const initialState: QaState = {
  runs: [],
  allRuns: [],
  allRunsTotal: 0,
  allRunsPage: 1,
  allRunsFlowFilter: null,
  currentRun: null,
  pendingRunId: null,
  loading: false,
  historyLoading: false,
  starting: false,
  error: null,
};

const qaSlice = createSlice({
  name: 'qa',
  initialState,
  reducers: {
    setPendingRunId: (state, action: PayloadAction<string | null>) => {
      state.pendingRunId = action.payload;
    },
    setAllRunsFlowFilter: (state, action: PayloadAction<string | null>) => {
      state.allRunsFlowFilter = action.payload;
      state.allRunsPage = 1;
    },
    setAllRunsPage: (state, action: PayloadAction<number>) => {
      state.allRunsPage = action.payload;
    },
    clearCurrentRun: (state) => {
      state.currentRun = null;
      state.pendingRunId = null;
      state.error = null;
    },
    resetQaState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(startQaRun.pending, (state) => {
        state.starting = true;
        state.error = null;
        state.currentRun = null;
        state.pendingRunId = null;
      })
      .addCase(startQaRun.fulfilled, (state, action) => {
        state.starting = false;
        state.pendingRunId = action.payload.runId;
      })
      .addCase(startQaRun.rejected, (state, action) => {
        state.starting = false;
        state.error = action.payload ?? 'Erro ao iniciar run';
      })

      .addCase(fetchQaRuns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQaRuns.fulfilled, (state, action) => {
        state.loading = false;
        state.runs = action.payload.items ?? (action.payload as unknown as QaRunListItem[]);
      })
      .addCase(fetchQaRuns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Erro ao carregar runs';
      })

      .addCase(fetchQaRun.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchQaRun.fulfilled, (state, action) => {
        state.currentRun = action.payload;
        const idx = state.runs.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          state.runs[idx] = { ...state.runs[idx], ...action.payload };
        } else {
          state.runs.unshift(action.payload);
        }
      })
      .addCase(fetchQaRun.rejected, (state, action) => {
        state.error = action.payload ?? 'Erro ao carregar run';
      })

      .addCase(fetchAllQaRuns.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(fetchAllQaRuns.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.allRuns = action.payload.items;
        state.allRunsTotal = action.payload.total;
        state.allRunsPage = action.payload.page;
      })
      .addCase(fetchAllQaRuns.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload ?? 'Erro ao carregar histórico';
      });
  },
});

export const { setAllRunsPage, clearCurrentRun, resetQaState } = qaSlice.actions;
export const qaReducer = qaSlice.reducer;
